/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Separator } from './ui/separator';
import { ChevronDown, ChevronUp, Guitar, Settings } from 'lucide-react';

interface InstrumentConfigProps {
  onConfigChange: (strings: number, frets: number, tuning: string[]) => void;
  currentStrings?: number;
  currentFrets?: number;
  currentTuning?: string[];
  isFree?: boolean;
}

const InstrumentConfig: React.FC<InstrumentConfigProps> = ({
  onConfigChange,
  currentStrings = 6,
  currentFrets = 22,
  currentTuning = ['E', 'A', 'D', 'G', 'B', 'E'],
  isFree = false
}) => {
  // Detect mobile for responsive design
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [strings, setStrings] = useState(currentStrings);
  const [frets, setFrets] = useState(currentFrets);
  const [tuning, setTuning] = useState([...currentTuning]);

  // Comprehensive tuning presets organized by category
  const tuningPresets = {
    standard: [
      { name: "Standard", tuning: ['E', 'A', 'D', 'G', 'B', 'E'], strings: 6, description: "Standard guitar tuning" },
      { name: "7-String", tuning: ['B', 'E', 'A', 'D', 'G', 'B', 'E'], strings: 7, description: "Extended range guitar" },
      { name: "8-String", tuning: ['F#', 'B', 'E', 'A', 'D', 'G', 'B', 'E'], strings: 8, description: "8-string guitar" },
      { name: "Mandolin", tuning: ['G', 'D', 'A', 'E'], strings: 4, description: "Standard mandolin tuning" },
      { name: "Ukulele", tuning: ['G', 'C', 'E', 'A'], strings: 4, description: "Standard ukulele tuning" },
      { name: "Banjo (4)", tuning: ['C', 'G', 'B', 'D'], strings: 4, description: "Tenor banjo tuning" },
      { name: "Banjo (5)", tuning: ['G', 'D', 'G', 'B', 'D'], strings: 5, description: "5-string banjo (Open G)" },
      { name: "Bass (4)", tuning: ['E', 'A', 'D', 'G'], strings: 4, description: "Standard bass tuning" },
      { name: "Bass (5)", tuning: ['B', 'E', 'A', 'D', 'G'], strings: 5, description: "5-string bass" }
    ],
    drop: [
      { name: "Drop D", tuning: ['D', 'A', 'D', 'G', 'B', 'E'], strings: 6, description: "Most common alternate tuning" },
      { name: "Drop C", tuning: ['C', 'G', 'C', 'F', 'A', 'D'], strings: 6, description: "Lower, heavier sound" },
      { name: "Drop B", tuning: ['B', 'F#', 'B', 'E', 'G#', 'C#'], strings: 6, description: "Very low tuning" },
      { name: "Double Drop D", tuning: ['D', 'A', 'D', 'G', 'B', 'D'], strings: 6, description: "Drop D with high E also dropped" }
    ],
    open: [
      { name: "Open G", tuning: ['D', 'G', 'D', 'G', 'B', 'D'], strings: 6, description: "Rolling Stones favorite" },
      { name: "Open D", tuning: ['D', 'A', 'D', 'F#', 'A', 'D'], strings: 6, description: "Great for slide guitar" },
      { name: "Open E", tuning: ['E', 'B', 'E', 'G#', 'B', 'E'], strings: 6, description: "Blues and slide guitar" },
      { name: "Open A", tuning: ['E', 'A', 'E', 'A', 'C#', 'E'], strings: 6, description: "Bright open tuning" },
      { name: "Open C", tuning: ['C', 'G', 'C', 'G', 'C', 'E'], strings: 6, description: "Folk and fingerstyle" }
    ],
    modal: [
      { name: "DADGAD", tuning: ['D', 'A', 'D', 'G', 'A', 'D'], strings: 6, description: "Celtic and fingerstyle favorite" },
      { name: "DADGBE", tuning: ['D', 'A', 'D', 'G', 'B', 'E'], strings: 6, description: "Hybrid of standard and drop D" },
      { name: "CGDGAD", tuning: ['C', 'G', 'D', 'G', 'A', 'D'], strings: 6, description: "Lower DADGAD variant" }
    ],
    experimental: [
      { name: "New Standard", tuning: ['C', 'G', 'D', 'A', 'E', 'G'], strings: 6, description: "Robert Fripp's creation" },
      { name: "All Fourths", tuning: ['E', 'A', 'D', 'G', 'C', 'F'], strings: 6, description: "Consistent intervals" },
      { name: "Ostrich", tuning: ['D', 'D', 'D', 'D', 'D', 'D'], strings: 6, description: "All strings same note" }
    ],
    steel: [
      { name: "C6 Lap Steel", tuning: ['C', 'E', 'G', 'A', 'C', 'E'], strings: 6, description: "Popular lap steel tuning" },
      { name: "A6 Lap Steel", tuning: ['C#', 'E', 'F#', 'A', 'C#', 'E'], strings: 6, description: "Country lap steel favorite" },
      { name: "E7 Lap Steel", tuning: ['B', 'D', 'E', 'F#', 'A', 'B'], strings: 6, description: "Blues lap steel tuning" },
      { name: "C6/Am7", tuning: ['G', 'C', 'E', 'G', 'A', 'C', 'E', 'G'], strings: 8, description: "Common pedal steel tuning" },
      { name: "E9 Pedal", tuning: ['B', 'D', 'E', 'F#', 'G#', 'B', 'E', 'F#', 'G#', 'B'], strings: 10, description: "Standard E9 pedal steel" }
    ]
  };

  // Adjust tuning array when string count changes
  useEffect(() => {
    const newTuning = [...tuning];
    if (newTuning.length < strings) {
      while (newTuning.length < strings) {
        newTuning.unshift(''); // Add new strings at the beginning
      }
    } else if (newTuning.length > strings) {
      newTuning.splice(0, newTuning.length - strings); // Remove extra strings from the beginning
    }
    setTuning(newTuning);
  }, [strings]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfigChange(strings, frets, tuning);
  };

  const handleTuningChange = (index: number, value: string) => {
    const newTuning = [...tuning];
    newTuning[index] = value.toUpperCase();
    setTuning(newTuning);
  };

  const handleStringsChange = (value: number) => {
    setStrings(Math.max(1, Math.min(12, value))); // Limit strings between 1 and 12
  };

  const applyPreset = (preset: { tuning: string[], strings: number }) => {
    setStrings(preset.strings);
    setTuning([...preset.tuning]);
    // Apply preset immediately without requiring Apply Configuration button
    onConfigChange(preset.strings, frets, preset.tuning);
  };

  const getCurrentTuningName = () => {
    const currentTuningString = tuning.join('');
    for (const category of Object.values(tuningPresets)) {
      for (const preset of category) {
        if (preset.tuning.join('') === currentTuningString && preset.strings === strings) {
          return preset.name;
        }
      }
    }
    return 'Custom';
  };

  const getCategoryDisplayName = (category: string) => {
    const categoryNames: { [key: string]: string } = {
      standard: 'Standard',
      drop: 'Drop Tunings',
      open: 'Open Tunings',
      modal: 'Modal & Celtic',
      experimental: 'Experimental',
      steel: 'Lap & Pedal Steel'
    };
    return categoryNames[category] || category;
  };

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="text-sm flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Guitar className="h-4 w-4" />
                Instrument Setup
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {getCurrentTuningName()}
                </Badge>
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent>
            {/* Reminder text */}
{/*             <div className="mb-4 p-3 bg-muted/30 rounded-lg border-l-4 border-primary">
              <p className="text-xs text-muted-foreground">
                💡 Remember to click "Apply Configuration" after making changes
              </p>
            </div> */}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Quick Tuning Presets */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Quick Presets
                </h3>
                
                <div className="space-y-3">
                  {Object.entries(tuningPresets).map(([category, presets]) => {
                    // On mobile, hide experimental and steel categories
                    if (isMobile && (category === 'experimental' || category === 'steel')) {
                      return null;
                    }

                    return (
                      <div key={category} className="space-y-2">
                        <h4 className="text-xs font-medium text-muted-foreground">
                          {getCategoryDisplayName(category)}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {presets.map((preset) => {
                            const isLocked = isFree && category !== 'standard';
                            return (
                              <Button
                                key={preset.name}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (isLocked) {
                                    alert('🔒 Upgrade to Pro to unlock all tuning presets!\n\nFree tier users have access to Standard tunings only. Upgrade to Pro or Premium to access Drop, Open, Modal, Experimental, and Steel tunings.');
                                    return;
                                  }
                                  applyPreset(preset);
                                }}
                                disabled={isLocked}
                                title={`${preset.description} (${preset.tuning.join('-')})`}
                                className="text-xs h-7 px-2"
                              >
                                {preset.name}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Manual Configuration */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Manual Configuration</h3>

                {/* String and Fret Controls */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium">Strings</label>
                    <Input
                      type="number"
                      value={strings}
                      onChange={(e) => {
                        if (isFree) {
                          alert('🔒 Upgrade to Pro to unlock manual configuration!\n\nFree tier users can select from Standard tuning presets. Upgrade to Pro or Premium to create custom configurations.');
                          return;
                        }
                        handleStringsChange(Number(e.target.value));
                      }}
                      min={1}
                      max={12}
                      className="text-center"
                      disabled={isFree}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium">Frets</label>
                    <Input
                      type="number"
                      value={frets}
                      onChange={(e) => {
                        if (isFree) {
                          alert('🔒 Upgrade to Pro to unlock manual configuration!\n\nFree tier users can select from Standard tuning presets. Upgrade to Pro or Premium to create custom configurations.');
                          return;
                        }
                        setFrets(Number(e.target.value));
                      }}
                      min={1}
                      max={24}
                      className="text-center"
                      disabled={isFree}
                    />
                  </div>
                </div>

                {/* String Tuning */}
                <div className="space-y-2">
                  <label className="text-xs font-medium">String Tuning (Low to High)</label>
                  <div className="grid grid-cols-4 gap-3 auto-rows-auto">
                    {tuning.map((note, index) => (
                      <div key={index} className="space-y-1">
                        <label className="text-xs text-muted-foreground text-center block">
                          {strings - index}
                        </label>
                        <Input
                          value={note}
                          onChange={(e) => {
                            if (isFree) {
                              alert('🔒 Upgrade to Pro to unlock manual configuration!\n\nFree tier users can select from Standard tuning presets. Upgrade to Pro or Premium to create custom configurations.');
                              return;
                            }
                            handleTuningChange(index, e.target.value);
                          }}
                          maxLength={3}
                          className="text-center text-sm h-9 px-2"
                          placeholder="Note"
                          disabled={isFree}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Apply Button */}
              <Button type="submit" className="w-full" disabled={isFree}>
                <Guitar className="h-4 w-4 mr-2" />
                Apply Configuration
              </Button>
              {isFree && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  All tunings available with{' '}
                  <a href="/pricing" className="text-primary hover:underline">Pro</a>
                </p>
              )}
            </form>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default InstrumentConfig;
